// Hyperparameter Tuning Component
// Advanced AI model configuration with real-time preview and auto-tuning recommendations

import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Slider } from '@/components/ui/slider';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Progress } from '@/components/ui/progress';
import { toast } from 'sonner';
import {
  Settings2, Sliders, RotateCcw, Play, Save, ChevronRight,
  TrendingUp, TrendingDown, Minus, AlertTriangle, CheckCircle2,
  Sparkles, History, Zap, Target, Brain, BarChart3, Lightbulb,
  RefreshCw, ArrowRight, Activity, ShieldCheck, Database
} from 'lucide-react';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, 
  ResponsiveContainer, Legend, Area, AreaChart
} from 'recharts';
import { useApiData } from '@/hooks';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Category icons
const categoryIcons = {
  weights: <Sliders className="h-4 w-4" />,
  learning: <Brain className="h-4 w-4" />,
  prediction: <Target className="h-4 w-4" />,
  data: <BarChart3 className="h-4 w-4" />
};

// Custom tooltip for charts
const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-slate-800 border border-slate-700 rounded-lg p-3 shadow-xl">
        <p className="text-white font-bold mb-2">{label}</p>
        {payload.map((entry, idx) => (
          <p key={idx} className="text-sm" style={{ color: entry.color }}>
            {entry.name}: {entry.value} hours
          </p>
        ))}
      </div>
    );
  }
  return null;
};

// Parameter Slider Component
const ParameterSlider = ({ param, definition, value, onChange, disabled }) => {
  const displayValue = definition.unit === '%' 
    ? `${(value * 100).toFixed(0)}%`
    : definition.unit 
      ? `${value}${definition.unit}`
      : value.toFixed(2);
  
  return (
    <div className="space-y-2">
      <div className="flex justify-between items-center">
        <Label className="text-slate-300 flex items-center gap-2">
          {categoryIcons[definition.category]}
          {definition.name}
        </Label>
        <span className="text-cyan-400 font-mono font-bold">{displayValue}</span>
      </div>
      <Slider
        value={[value]}
        onValueChange={([v]) => onChange(param, v)}
        min={definition.min}
        max={definition.max}
        step={definition.step}
        disabled={disabled}
        className="cursor-pointer"
      />
      <p className="text-xs text-slate-500">{definition.description}</p>
    </div>
  );
};

// Preset Card Component
const PresetCard = ({ preset, name, isActive, onApply, disabled }) => {
  return (
    <Card 
      className={`cursor-pointer transition-all ${
        isActive 
          ? 'bg-cyan-500/20 border-cyan-500' 
          : 'bg-slate-800/50 border-slate-700 hover:border-slate-600'
      } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
      onClick={() => !disabled && onApply(name)}
    >
      <CardContent className="p-4">
        <div className="flex items-center justify-between mb-2">
          <h4 className="text-white font-medium">{preset.name}</h4>
          {isActive && <CheckCircle2 className="h-4 w-4 text-cyan-400" />}
        </div>
        <p className="text-sm text-slate-400">{preset.description}</p>
      </CardContent>
    </Card>
  );
};

// Comparison Chart
const ComparisonChart = ({ data }) => {
  if (!data) return null;
  
  const chartData = data.current_predictions.map((curr, idx) => ({
    week: curr.week.split('-').slice(1).join('/'),
    current: curr.hours,
    test: data.test_predictions[idx]?.hours || 0
  }));
  
  return (
    <div className="h-64">
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={chartData}>
          <defs>
            <linearGradient id="currentGradient" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="#6366f1" stopOpacity={0.4}/>
              <stop offset="95%" stopColor="#6366f1" stopOpacity={0}/>
            </linearGradient>
            <linearGradient id="testGradient" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="#06b6d4" stopOpacity={0.4}/>
              <stop offset="95%" stopColor="#06b6d4" stopOpacity={0}/>
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
          <XAxis dataKey="week" stroke="#94a3b8" fontSize={12} />
          <YAxis stroke="#94a3b8" fontSize={12} />
          <Tooltip content={<CustomTooltip />} />
          <Legend />
          <Area 
            type="monotone" 
            dataKey="current" 
            name="Current Params"
            stroke="#6366f1" 
            fill="url(#currentGradient)" 
            strokeWidth={2}
          />
          <Area 
            type="monotone" 
            dataKey="test" 
            name="New Params"
            stroke="#06b6d4" 
            fill="url(#testGradient)" 
            strokeWidth={2}
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
};

// Main Component
export default function HyperparameterTuning({ token }) {
  const [activeTab, setActiveTab] = useState('recommendations');
  const [params, setParams] = useState({});
  const [hasChanges, setHasChanges] = useState(false);
  const [testing, setTesting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testResults, setTestResults] = useState(null);
  const [applyingRec, setApplyingRec] = useState(null);
  
  // Fetch current hyperparameters
  const { data: hyperData, loading: hyperLoading, refetch: refetchHyper } = useApiData(
    '/api/model/hyperparameters',
    { cache: false }
  );
  
  // Fetch presets
  const { data: presetsData, loading: presetsLoading } = useApiData(
    '/api/model/hyperparameters/presets',
    { cache: true }
  );
  
  // Fetch history
  const { data: historyData, loading: historyLoading, refetch: refetchHistory } = useApiData(
    '/api/model/hyperparameters/history?limit=10',
    { cache: false }
  );
  
  // Fetch recommendations
  const { data: recsData, loading: recsLoading, refetch: refetchRecs } = useApiData(
    '/api/model/hyperparameters/recommendations',
    { cache: false }
  );
  
  // Initialize params when data loads
  useEffect(() => {
    if (hyperData?.hyperparameters) {
      setParams(hyperData.hyperparameters);
      setHasChanges(false);
    }
  }, [hyperData]);
  
  const definitions = hyperData?.definitions || {};
  const presets = presetsData?.presets || {};
  const history = historyData?.history || [];
  const recommendations = recsData?.recommendations || [];
  const analysis = recsData?.analysis || {};
  
  // Apply single recommendation
  const handleApplyRecommendation = async (recId) => {
    setApplyingRec(recId);
    try {
      const response = await fetch(`${API_URL}/api/model/hyperparameters/recommendations/${recId}/apply`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      const result = await response.json();
      
      if (result.success) {
        toast.success(result.message);
        refetchHyper();
        refetchRecs();
        refetchHistory();
      } else {
        toast.error(result.error || 'Failed to apply recommendation');
      }
    } catch (error) {
      console.error('Apply rec error:', error);
      toast.error('Failed to apply recommendation');
    } finally {
      setApplyingRec(null);
    }
  };
  
  // Apply all recommendations
  const handleApplyAllRecommendations = async (priority = 'all') => {
    setApplyingRec('all');
    try {
      const response = await fetch(`${API_URL}/api/model/hyperparameters/recommendations/apply-all?priority=${priority}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      const result = await response.json();
      
      if (result.success) {
        toast.success(`Applied ${result.applied_count} recommendations`);
        refetchHyper();
        refetchRecs();
        refetchHistory();
      } else {
        toast.error(result.error || 'Failed to apply recommendations');
      }
    } catch (error) {
      console.error('Apply all recs error:', error);
      toast.error('Failed to apply recommendations');
    } finally {
      setApplyingRec(null);
    }
  };
  
  // Handle parameter change
  const handleParamChange = (param, value) => {
    setParams(prev => ({ ...prev, [param]: value }));
    setHasChanges(true);
    setTestResults(null);
  };
  
  // Test parameters
  const handleTest = async () => {
    setTesting(true);
    try {
      const response = await fetch(`${API_URL}/api/model/hyperparameters/test`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(params)
      });
      
      const result = await response.json();
      
      if (result.success) {
        setTestResults(result.comparison);
        toast.success('Parameters tested successfully');
      } else {
        toast.error(result.error || 'Test failed');
      }
    } catch (error) {
      console.error('Test error:', error);
      toast.error('Failed to test parameters');
    } finally {
      setTesting(false);
    }
  };
  
  // Save parameters
  const handleSave = async () => {
    setSaving(true);
    try {
      const response = await fetch(`${API_URL}/api/model/hyperparameters`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(params)
      });
      
      const result = await response.json();
      
      if (result.success) {
        toast.success('Hyperparameters saved successfully');
        setHasChanges(false);
        refetchHyper();
        refetchHistory();
      } else {
        toast.error(result.error || 'Save failed');
      }
    } catch (error) {
      console.error('Save error:', error);
      toast.error('Failed to save parameters');
    } finally {
      setSaving(false);
    }
  };
  
  // Reset to defaults
  const handleReset = async () => {
    if (!window.confirm('Reset all hyperparameters to default values?')) return;
    
    try {
      const response = await fetch(`${API_URL}/api/model/hyperparameters/reset`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      const result = await response.json();
      
      if (result.success) {
        toast.success('Reset to defaults');
        setParams(result.hyperparameters);
        setHasChanges(false);
        setTestResults(null);
        refetchHyper();
        refetchHistory();
      } else {
        toast.error(result.error || 'Reset failed');
      }
    } catch (error) {
      console.error('Reset error:', error);
      toast.error('Failed to reset parameters');
    }
  };
  
  // Apply preset
  const handleApplyPreset = async (presetName) => {
    try {
      const response = await fetch(`${API_URL}/api/model/hyperparameters/presets/${presetName}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      const result = await response.json();
      
      if (result.success) {
        toast.success(`Applied "${result.preset}" preset`);
        setParams(result.hyperparameters);
        setHasChanges(false);
        setTestResults(null);
        refetchHyper();
        refetchHistory();
      } else {
        toast.error(result.error || 'Failed to apply preset');
      }
    } catch (error) {
      console.error('Preset error:', error);
      toast.error('Failed to apply preset');
    }
  };
  
  // Calculate weights sum
  const weightsSum = ['seasonal_weight', 'census_trend_weight', 'admission_pattern_weight', 
                      'staff_availability_weight', 'accuracy_adjustment_weight']
    .reduce((sum, key) => sum + (params[key] || 0), 0);
  
  // Group parameters by category
  const paramsByCategory = Object.entries(definitions).reduce((acc, [key, def]) => {
    const cat = def.category;
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push({ key, ...def });
    return acc;
  }, {});
  
  if (hyperLoading) {
    return (
      <div className="space-y-4">
        {[1, 2, 3].map(i => (
          <div key={i} className="h-32 bg-slate-800/50 rounded-lg animate-pulse" />
        ))}
      </div>
    );
  }
  
  return (
    <div className="space-y-6">
      {/* Header */}
      <Alert className="bg-gradient-to-r from-indigo-900/40 to-violet-900/40 border-indigo-500/30">
        <Settings2 className="h-4 w-4 text-indigo-400" />
        <AlertTitle className="text-indigo-400 font-bold">Model Hyperparameter Tuning</AlertTitle>
        <AlertDescription className="text-indigo-200 text-sm">
          Fine-tune the AI prediction model by adjusting weights, learning parameters, and thresholds.
          Test changes before applying to see their impact on predictions.
        </AlertDescription>
      </Alert>
      
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-slate-800/50 border border-slate-700/50">
          <TabsTrigger value="recommendations" data-testid="recommendations-tab" className="data-[state=active]:bg-emerald-500/20 data-[state=active]:text-emerald-400">
            <Lightbulb className="h-4 w-4 mr-2" />
            Auto-Tune
            {recommendations.length > 0 && (
              <Badge className="ml-2 bg-emerald-500/30 text-emerald-400 text-xs">{recommendations.length}</Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="tuning" className="data-[state=active]:bg-indigo-500/20 data-[state=active]:text-indigo-400">
            <Sliders className="h-4 w-4 mr-2" />
            Parameter Tuning
          </TabsTrigger>
          <TabsTrigger value="presets" className="data-[state=active]:bg-indigo-500/20 data-[state=active]:text-indigo-400">
            <Sparkles className="h-4 w-4 mr-2" />
            Presets
          </TabsTrigger>
          <TabsTrigger value="history" className="data-[state=active]:bg-indigo-500/20 data-[state=active]:text-indigo-400">
            <History className="h-4 w-4 mr-2" />
            History
          </TabsTrigger>
        </TabsList>
        
        {/* Recommendations Tab */}
        <TabsContent value="recommendations" className="mt-6 space-y-6">
          {/* Health Score Card */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Card className={`border ${
              analysis.overall_health === 'excellent' ? 'bg-emerald-500/10 border-emerald-500/30' :
              analysis.overall_health === 'good' ? 'bg-blue-500/10 border-blue-500/30' :
              analysis.overall_health === 'fair' ? 'bg-amber-500/10 border-amber-500/30' :
              'bg-rose-500/10 border-rose-500/30'
            }`}>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className={`p-2 rounded-lg ${
                    analysis.overall_health === 'excellent' ? 'bg-emerald-500/20' :
                    analysis.overall_health === 'good' ? 'bg-blue-500/20' :
                    analysis.overall_health === 'fair' ? 'bg-amber-500/20' :
                    'bg-rose-500/20'
                  }`}>
                    <Activity className={`h-5 w-5 ${
                      analysis.overall_health === 'excellent' ? 'text-emerald-400' :
                      analysis.overall_health === 'good' ? 'text-blue-400' :
                      analysis.overall_health === 'fair' ? 'text-amber-400' :
                      'text-rose-400'
                    }`} />
                  </div>
                  <div>
                    <p className="text-sm text-slate-400">Model Health</p>
                    <p className="text-xl font-bold text-white capitalize">{analysis.overall_health || 'Analyzing...'}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card className="bg-slate-800/50 border-slate-700/50">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-indigo-500/20 rounded-lg">
                    <Target className="h-5 w-5 text-indigo-400" />
                  </div>
                  <div>
                    <p className="text-sm text-slate-400">Health Score</p>
                    <p className="text-xl font-bold text-white">{analysis.health_score || 0}%</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card className="bg-slate-800/50 border-slate-700/50">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-cyan-500/20 rounded-lg">
                    <Database className="h-5 w-5 text-cyan-400" />
                  </div>
                  <div>
                    <p className="text-sm text-slate-400">Data Analyzed</p>
                    <p className="text-xl font-bold text-white">{analysis.data_points_analyzed || 0}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card className="bg-slate-800/50 border-slate-700/50">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-amber-500/20 rounded-lg">
                    <Lightbulb className="h-5 w-5 text-amber-400" />
                  </div>
                  <div>
                    <p className="text-sm text-slate-400">Recommendations</p>
                    <p className="text-xl font-bold text-white">{recommendations.length}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
          
          {/* Recommendations List */}
          <Card className="bg-slate-900/60 border-slate-700/50">
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="text-white flex items-center gap-2">
                  <Lightbulb className="h-5 w-5 text-amber-400" />
                  Auto-Tuning Recommendations
                </CardTitle>
                <CardDescription className="text-slate-400">
                  AI-analyzed suggestions to optimize model performance
                </CardDescription>
              </div>
              <div className="flex gap-2">
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={() => refetchRecs()}
                  className="text-slate-300 border-slate-600"
                >
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Refresh
                </Button>
                {recommendations.filter(r => r.parameter).length > 0 && (
                  <Button 
                    size="sm" 
                    onClick={() => handleApplyAllRecommendations('high')}
                    disabled={applyingRec !== null}
                    className="bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600"
                    data-testid="apply-all-recs-btn"
                  >
                    {applyingRec === 'all' ? (
                      <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <Zap className="h-4 w-4 mr-2" />
                    )}
                    Apply All High Priority
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent>
              {recsLoading ? (
                <div className="space-y-4">
                  {[1, 2, 3].map(i => (
                    <div key={i} className="h-24 bg-slate-800/50 rounded-lg animate-pulse" />
                  ))}
                </div>
              ) : recommendations.length === 0 ? (
                <div className="text-center py-12">
                  <ShieldCheck className="h-16 w-16 text-emerald-400 mx-auto mb-4" />
                  <h3 className="text-xl font-bold text-white mb-2">Model is Optimized!</h3>
                  <p className="text-slate-400">No recommendations at this time. Your model parameters are well-tuned.</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {recommendations.map((rec, idx) => (
                    <Card key={rec.id || idx} className={`border ${
                      rec.priority === 'high' ? 'bg-rose-500/10 border-rose-500/30' :
                      rec.priority === 'medium' ? 'bg-amber-500/10 border-amber-500/30' :
                      'bg-slate-800/50 border-slate-700/50'
                    }`}>
                      <CardContent className="p-4">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              <Badge className={`${
                                rec.priority === 'high' ? 'bg-rose-500/20 text-rose-400' :
                                rec.priority === 'medium' ? 'bg-amber-500/20 text-amber-400' :
                                'bg-slate-500/20 text-slate-400'
                              }`}>
                                {rec.priority?.toUpperCase()}
                              </Badge>
                              <Badge className="bg-indigo-500/20 text-indigo-400">
                                {rec.category}
                              </Badge>
                              {rec.confidence && (
                                <span className="text-xs text-slate-500">
                                  {Math.round(rec.confidence * 100)}% confidence
                                </span>
                              )}
                            </div>
                            
                            <h4 className="text-white font-medium mb-1">{rec.title}</h4>
                            <p className="text-sm text-slate-400 mb-3">{rec.description}</p>
                            
                            {rec.parameter && (
                              <div className="flex items-center gap-4 text-sm">
                                <span className="text-slate-500">
                                  <span className="text-slate-400">{rec.parameter}:</span>{' '}
                                  <span className="text-rose-400">{rec.current_value}</span>
                                </span>
                                <ArrowRight className="h-4 w-4 text-slate-500" />
                                <span className="text-emerald-400 font-medium">{rec.suggested_value}</span>
                              </div>
                            )}
                            
                            {rec.expected_improvement && (
                              <p className="text-xs text-cyan-400 mt-2">
                                Expected: {rec.expected_improvement}
                              </p>
                            )}
                          </div>
                          
                          <Button
                            size="sm"
                            onClick={() => handleApplyRecommendation(rec.id)}
                            disabled={applyingRec !== null}
                            className={rec.parameter ? 'bg-emerald-500 hover:bg-emerald-600' : 'bg-slate-600 hover:bg-slate-500'}
                            data-testid={`apply-rec-${rec.id}`}
                          >
                            {applyingRec === rec.id ? (
                              <RefreshCw className="h-4 w-4 animate-spin" />
                            ) : rec.action ? (
                              <>View</>
                            ) : (
                              <>Apply</>
                            )}
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
        
        {/* Tuning Tab */}
        <TabsContent value="tuning" className="mt-6 space-y-6">
          {/* Weights Balance Indicator */}
          <Card className="bg-slate-900/60 border-slate-700/50">
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-slate-300 font-medium">Prediction Weights Balance</span>
                <Badge className={
                  Math.abs(weightsSum - 1) < 0.05 
                    ? 'bg-emerald-500/20 text-emerald-400' 
                    : 'bg-amber-500/20 text-amber-400'
                }>
                  {(weightsSum * 100).toFixed(0)}%
                </Badge>
              </div>
              <Progress value={weightsSum * 100} className="h-2" />
              <p className="text-xs text-slate-500 mt-2">
                {Math.abs(weightsSum - 1) < 0.05 
                  ? 'Weights are balanced (sum ≈ 100%)'
                  : 'Weights will be auto-normalized on save'}
              </p>
            </CardContent>
          </Card>
          
          {/* Parameter Sections */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Weights Section */}
            <Card className="bg-slate-900/60 border-slate-700/50">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Sliders className="h-5 w-5 text-indigo-400" />
                  Prediction Weights
                </CardTitle>
                <CardDescription className="text-slate-400">
                  Adjust the importance of different factors in predictions
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {paramsByCategory.weights?.map(({ key, ...def }) => (
                  <ParameterSlider
                    key={key}
                    param={key}
                    definition={def}
                    value={params[key] || def.min}
                    onChange={handleParamChange}
                    disabled={saving}
                  />
                ))}
              </CardContent>
            </Card>
            
            {/* Learning & Prediction Section */}
            <Card className="bg-slate-900/60 border-slate-700/50">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Brain className="h-5 w-5 text-violet-400" />
                  Learning & Prediction
                </CardTitle>
                <CardDescription className="text-slate-400">
                  Control model adaptation and forecast behavior
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {[...paramsByCategory.learning || [], ...paramsByCategory.prediction || []].map(({ key, ...def }) => (
                  <ParameterSlider
                    key={key}
                    param={key}
                    definition={def}
                    value={params[key] || def.min}
                    onChange={handleParamChange}
                    disabled={saving}
                  />
                ))}
              </CardContent>
            </Card>
            
            {/* Data Quality Section */}
            <Card className="bg-slate-900/60 border-slate-700/50 lg:col-span-2">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <BarChart3 className="h-5 w-5 text-cyan-400" />
                  Data Quality Settings
                </CardTitle>
                <CardDescription className="text-slate-400">
                  Configure data validation and outlier handling
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {paramsByCategory.data?.map(({ key, ...def }) => (
                    <ParameterSlider
                      key={key}
                      param={key}
                      definition={def}
                      value={params[key] || def.min}
                      onChange={handleParamChange}
                      disabled={saving}
                    />
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
          
          {/* Test Results */}
          {testResults && (
            <Card className="bg-gradient-to-br from-slate-900/80 to-indigo-900/20 border-indigo-500/30">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <TrendingUp className="h-5 w-5 text-cyan-400" />
                  Prediction Comparison
                </CardTitle>
                <CardDescription className="text-slate-400">
                  Impact of new parameters vs current settings
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <ComparisonChart data={testResults} />
                
                <div className="grid grid-cols-3 gap-4">
                  <div className="p-4 rounded-lg bg-slate-800/50">
                    <p className="text-sm text-slate-400">Current Avg</p>
                    <p className="text-2xl font-bold text-indigo-400">{testResults.current_avg_hours} hrs</p>
                  </div>
                  <div className="p-4 rounded-lg bg-slate-800/50">
                    <p className="text-sm text-slate-400">New Avg</p>
                    <p className="text-2xl font-bold text-cyan-400">{testResults.test_avg_hours} hrs</p>
                  </div>
                  <div className="p-4 rounded-lg bg-slate-800/50">
                    <p className="text-sm text-slate-400">Difference</p>
                    <div className="flex items-center gap-2">
                      {testResults.impact === 'increase' ? (
                        <TrendingUp className="h-5 w-5 text-amber-400" />
                      ) : testResults.impact === 'decrease' ? (
                        <TrendingDown className="h-5 w-5 text-emerald-400" />
                      ) : (
                        <Minus className="h-5 w-5 text-slate-400" />
                      )}
                      <p className={`text-2xl font-bold ${
                        testResults.impact === 'increase' ? 'text-amber-400' :
                        testResults.impact === 'decrease' ? 'text-emerald-400' :
                        'text-slate-400'
                      }`}>
                        {testResults.difference_percent > 0 ? '+' : ''}{testResults.difference_percent}%
                      </p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
          
          {/* Action Buttons */}
          <div className="flex justify-between items-center">
            <Button
              variant="outline"
              onClick={handleReset}
              className="text-slate-300 border-slate-600"
              data-testid="reset-params-btn"
            >
              <RotateCcw className="h-4 w-4 mr-2" />
              Reset to Defaults
            </Button>
            
            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={handleTest}
                disabled={!hasChanges || testing}
                className="text-indigo-400 border-indigo-500/50 hover:bg-indigo-500/10"
                data-testid="test-params-btn"
              >
                {testing ? (
                  <Settings2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Play className="h-4 w-4 mr-2" />
                )}
                Test Parameters
              </Button>
              
              <Button
                onClick={handleSave}
                disabled={!hasChanges || saving}
                className="bg-gradient-to-r from-indigo-500 to-violet-500 hover:from-indigo-600 hover:to-violet-600"
                data-testid="save-params-btn"
              >
                {saving ? (
                  <Settings2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Save className="h-4 w-4 mr-2" />
                )}
                Save Parameters
              </Button>
            </div>
          </div>
        </TabsContent>
        
        {/* Presets Tab */}
        <TabsContent value="presets" className="mt-6">
          <Card className="bg-slate-900/60 border-slate-700/50">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <Sparkles className="h-5 w-5 text-amber-400" />
                Preset Configurations
              </CardTitle>
              <CardDescription className="text-slate-400">
                Quick-apply optimized parameter sets for different use cases
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {Object.entries(presets).map(([name, preset]) => (
                  <PresetCard
                    key={name}
                    name={name}
                    preset={preset}
                    isActive={JSON.stringify(params) === JSON.stringify(preset.params)}
                    onApply={handleApplyPreset}
                    disabled={saving}
                  />
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
        
        {/* History Tab */}
        <TabsContent value="history" className="mt-6">
          <Card className="bg-slate-900/60 border-slate-700/50">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <History className="h-5 w-5 text-emerald-400" />
                Parameter Change History
              </CardTitle>
              <CardDescription className="text-slate-400">
                Track changes to hyperparameters over time
              </CardDescription>
            </CardHeader>
            <CardContent>
              {historyLoading ? (
                <div className="space-y-4">
                  {[1, 2, 3].map(i => (
                    <div key={i} className="h-16 bg-slate-800/50 rounded-lg animate-pulse" />
                  ))}
                </div>
              ) : history.length === 0 ? (
                <div className="text-center py-8">
                  <History className="h-12 w-12 text-slate-600 mx-auto mb-4" />
                  <p className="text-slate-400">No parameter changes recorded yet</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {history.map((entry, idx) => (
                    <div 
                      key={idx} 
                      className="p-4 rounded-lg bg-slate-800/50 border border-slate-700/50"
                    >
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-white font-medium">
                          {entry.action?.includes('preset') ? (
                            <span className="flex items-center gap-2">
                              <Sparkles className="h-4 w-4 text-amber-400" />
                              Applied Preset: {entry.action.replace('applied_preset_', '')}
                            </span>
                          ) : entry.action === 'reset_to_defaults' ? (
                            <span className="flex items-center gap-2">
                              <RotateCcw className="h-4 w-4 text-slate-400" />
                              Reset to Defaults
                            </span>
                          ) : (
                            <span className="flex items-center gap-2">
                              <Settings2 className="h-4 w-4 text-indigo-400" />
                              Manual Update
                            </span>
                          )}
                        </span>
                        <span className="text-xs text-slate-500">
                          {new Date(entry.timestamp).toLocaleString()}
                        </span>
                      </div>
                      {entry.changes && Object.keys(entry.changes).length > 0 && (
                        <div className="flex flex-wrap gap-2 mt-2">
                          {Object.entries(entry.changes).map(([key, value]) => (
                            <Badge key={key} className="bg-slate-700/50 text-slate-300 text-xs">
                              {key}: {typeof value === 'number' ? value.toFixed(2) : value}
                            </Badge>
                          ))}
                        </div>
                      )}
                      <p className="text-xs text-slate-500 mt-2">
                        By: {entry.updated_by || 'System'}
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
